import { supabase } from './supabase';

// Removed all cache-related code - using TanStack Query only

/**
 * Template Category interface
 */
export interface TemplateCategory {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  is_visible_in_dropdown: boolean;
  created_at: string;
  updated_at: string | null;
}

/**
 * Template interface
 */
export interface Template {
  id: number;
  name: string;
  description: string | null;
  is_public: boolean;
  category_id?: number;
  category_name?: string;
  created_at: string;
  updated_at: string | null;
}

/**
 * Template Section interface
 */
export interface TemplateSection {
  id: number;
  template_id: number;
  name: string;
  order_position: number;
  created_at: string;
  updated_at: string | null;
}

/**
 * Template Field interface
 */
export interface TemplateField {
  id: number;
  template_id: number;
  section_id: number | null;
  field_name: string;
  field_type: 'text' | 'textarea' | 'number' | 'dropdown' | 'checkbox' | 'date';
  is_required: boolean;
  default_value: string | null;
  options: string | string[] | null; // For dropdown options, stored as JSON array or comma-separated values
  validation_rules: any | null;
  order_position: number;
  created_at: string;
  updated_at: string | null;
}

/**
 * Template with Fields interface
 */
export interface TemplateWithFields extends Template {
  sections: TemplateSection[];
  fields: TemplateField[];
}

/**
 * Get all template categories
 * @param projectId - Project ID
 * @returns Promise with array of template categories
 */
export const getTemplateCategories = async (projectId?: number): Promise<TemplateCategory[]> => {
  // Fetch from API - no caching at service level
  let query = supabase
    .from('template_categories')
    .select('*')
    .order('name');

  // Filter by project_id if provided
  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching template categories:', error);
    throw error;
  }

  return data;
};

/**
 * Get visible template categories (for dropdowns)
 * @returns Promise with array of visible template categories
 */
export const getVisibleTemplateCategories = async (): Promise<TemplateCategory[]> => {
  // Fetch from API - no caching at service level
  const { data, error } = await supabase
    .from('template_categories')
    .select('*')
    .eq('is_visible_in_dropdown', true)
    .order('name');

  if (error) {
    console.error('Error fetching visible template categories:', error);
    throw error;
  }

  // Cache the result (we don't cache all categories here)
  return data;
};

/**
 * Get template category by ID
 * @param categoryId - Category ID
 * @returns Promise with template category
 */
export const getTemplateCategoryById = async (categoryId: number | string): Promise<TemplateCategory> => {
  // Fetch from API - no caching at service level
  const { data, error } = await supabase
    .from('template_categories')
    .select('*')
    .eq('id', categoryId)
    .single();

  if (error) {
    console.error('Error fetching template category:', error);
    throw error;
  }

  return data;
};

/**
 * Create template category
 * @param category - Category data
 * @returns Promise with created category
 */
export const createTemplateCategory = async (
  category: {
    name: string;
    description?: string;
    icon?: string;
    is_visible_in_dropdown?: boolean;
    project_id: number;
  }
): Promise<TemplateCategory> => {
  const { data, error } = await supabase
    .from('template_categories')
    .insert([
      {
        name: category.name,
        description: category.description || null,
        icon: category.icon || null,
        is_visible_in_dropdown: category.is_visible_in_dropdown !== undefined ? category.is_visible_in_dropdown : true,
        project_id: category.project_id,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating template category:', error);
    throw error;
  }

  return data;
};

/**
 * Update template category
 * @param categoryId - Category ID
 * @param category - Updated category data
 * @returns Promise with updated category
 */
export const updateTemplateCategory = async (
  categoryId: number | string,
  category: Partial<TemplateCategory>
): Promise<TemplateCategory> => {
  const { data, error } = await supabase
    .from('template_categories')
    .update(category)
    .eq('id', categoryId)
    .select()
    .single();

  if (error) {
    console.error('Error updating template category:', error);
    throw error;
  }

  return data;
};

/**
 * Delete template category
 * @param categoryId - Category ID
 */
export const deleteTemplateCategory = async (categoryId: number | string): Promise<void> => {
  const { error } = await supabase
    .from('template_categories')
    .delete()
    .eq('id', categoryId);

  if (error) {
    console.error('Error deleting template category:', error);
    throw error;
  }
};

/**
 * Get all templates
 * @param projectId - Project ID
 * @returns Promise with array of templates
 */
export const getTemplates = async (projectId?: number): Promise<Template[]> => {
  // Fetch from API - no caching at service level
  let query = supabase
    .from('templates')
    .select('*')
    .order('name');

  // Filter by project_id if provided
  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }

  // Use the data directly as it now matches the Template interface
  const templates: Template[] = data;

  return templates;
};



/**
 * Get template by ID
 * @param templateId - Template ID
 * @returns Promise with template
 */
export const getTemplateById = async (templateId: number | string): Promise<Template> => {
  // Fetch from API - no caching at service level
  const { data, error } = await supabase
    .from('templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) {
    console.error('Error fetching template:', error);
    throw error;
  }

  // Use the data directly as it now matches the Template interface
  const template: Template = data;

  return template;
};

/**
 * Get template with fields and sections
 * @param templateId - Template ID
 * @returns Promise with template including fields and sections
 */
export const getTemplateWithFields = async (templateId: number | string): Promise<TemplateWithFields> => {
  // Get the template
  const template = await getTemplateById(templateId);

  // Fetch sections
  const { data: sectionsData, error: sectionsError } = await supabase
    .from('template_sections')
    .select('*')
    .eq('template_id', templateId)
    .order('order_position');

  if (sectionsError) {
    console.error('Error fetching template sections:', sectionsError);
    throw sectionsError;
  }

  const sections = sectionsData;

  // Fetch fields
  const { data: fieldsData, error: fieldsError } = await supabase
    .from('template_fields')
    .select('*')
    .eq('template_id', templateId)
    .order('order_position');

  if (fieldsError) {
    console.error('Error fetching template fields:', fieldsError);
    throw fieldsError;
  }

  const fields = fieldsData;

  return {
    ...template,
    sections: sections || [],
    fields: fields || []
  };
};

/**
 * Create template
 * @param template - Template data
 * @returns Promise with created template
 */
export const createTemplate = async (
  template: {
    name: string;
    description?: string;
    is_public?: boolean;
    project_id: number;
  }
): Promise<Template> => {

  if(template.project_id === undefined){
    throw new Error('Project ID is required to create a template');
  }

  // Create the template
  const { data, error } = await supabase
    .from('templates')
    .insert([
      {
        name: template.name,
        description: template.description || null,
        is_public: template.is_public !== undefined ? template.is_public : true,
        project_id: template.project_id,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating template:', error);
    throw error;
  }

  return data;
};

/**
 * Update template
 * @param templateId - Template ID
 * @param template - Updated template data
 * @returns Promise with updated template
 */
export const updateTemplate = async (
  templateId: number | string,
  template: Partial<Template>
): Promise<Template> => {
  try {
    // Log the request for debugging
    console.log('Updating template:', templateId, template);

    const { data, error, status, statusText } = await supabase
      .from('templates')
      .update(template)
      .eq('id', templateId)
      .select()
      .single();

    if (error) {
      console.error('Error updating template:', error);
      console.error('Status:', status, statusText);
      throw new Error(`Failed to update template: ${error.message} (${status} ${statusText})`);
    }

    if (!data) {
      throw new Error('No data returned from update operation');
    }

    return data;
  } catch (error) {
    console.error('Exception in updateTemplate:', error);
    throw error;
  }
};

/**
 * Delete template
 * @param templateId - Template ID
 * @param projectId - Project ID (optional, for better cache clearing)
 */
export const deleteTemplate = async (templateId: number | string, projectId?: number): Promise<void> => {
  try {
    console.log('Attempting to delete template:', templateId);

    const { data, error, status, statusText } = await supabase
      .from('templates')
      .delete()
      .eq('id', templateId)
      .select(); // Add select to get confirmation of deletion

    if (error) {
      console.error('Error deleting template:', error);
      console.error('Status:', status, statusText);
      throw new Error(`Failed to delete template: ${error.message} (${status} ${statusText})`);
    }

    console.log('Template deletion successful:', data);

    // Cache clearing handled by TanStack Query
  } catch (error) {
    console.error('Exception in deleteTemplate:', error);
    throw error;
  }
};

/**
 * Create template section
 * @param section - Section data
 * @returns Promise with created section
 */
export const createTemplateSection = async (
  section: {
    template_id: number | string;
    name: string;
    order_position?: number;
  }
): Promise<TemplateSection> => {
  // Validate template_id
  if (!section.template_id || section.template_id === 'undefined') {
    throw new Error('Valid template_id is required to create a section');
  }

  // Convert template_id to number to ensure it's a valid bigint
  const templateId = Number(section.template_id);

  // Get the highest order position
  const { data: sections } = await supabase
    .from('template_sections')
    .select('order_position')
    .eq('template_id', templateId)
    .order('order_position', { ascending: false })
    .limit(1);

  const orderPosition = sections && sections.length > 0
    ? sections[0].order_position + 1
    : 0;

  const { data, error } = await supabase
    .from('template_sections')
    .insert([
      {
        template_id: templateId,
        name: section.name,
        order_position: section.order_position !== undefined ? section.order_position : orderPosition,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating template section:', error);
    throw error;
  }

  return data;
};

/**
 * Update template section
 * @param sectionId - Section ID
 * @param section - Updated section data
 * @param templateId - Template ID (for cache clearing)
 * @returns Promise with updated section
 */
export const updateTemplateSection = async (
  sectionId: number | string,
  section: Partial<TemplateSection>,
  templateId: number | string
): Promise<TemplateSection> => {
  try {
    // Log the request for debugging
    console.log('Updating template section:', sectionId, section);

    const { data, error, status, statusText } = await supabase
      .from('template_sections')
      .update(section)
      .eq('id', sectionId)
      .select()
      .single();

    if (error) {
      console.error('Error updating template section:', error);
      console.error('Status:', status, statusText);
      throw new Error(`Failed to update template section: ${error.message} (${status} ${statusText})`);
    }

    if (!data) {
      throw new Error('No data returned from update operation');
    }

    return data;
  } catch (error) {
    console.error('Exception in updateTemplateSection:', error);
    throw error;
  }
};

/**
 * Delete template section
 * @param sectionId - Section ID
 * @param templateId - Template ID (for cache clearing)
 */
export const deleteTemplateSection = async (
  sectionId: number | string,
  templateId: number | string
): Promise<void> => {
  const { error } = await supabase
    .from('template_sections')
    .delete()
    .eq('id', sectionId);

  if (error) {
    console.error('Error deleting template section:', error);
    throw error;
  }

  // Cache clearing handled by TanStack Query
};

/**
 * Create template field
 * @param field - Field data
 * @returns Promise with created field
 */
export const createTemplateField = async (
  field: {
    template_id: number | string;
    section_id?: number | string | null;
    field_name: string;
    field_type: 'text' | 'textarea' | 'number' | 'dropdown' | 'checkbox' | 'date';
    is_required?: boolean;
    default_value?: string | null;
    options?: string | string[] | null;
    validation_rules?: any | null;
    order_position?: number;
  }
): Promise<TemplateField> => {
  // Validate template_id
  if (!field.template_id || field.template_id === 'undefined') {
    throw new Error('Valid template_id is required to create a field');
  }

  // Convert template_id and section_id to numbers to ensure they're valid bigints
  const templateId = Number(field.template_id);
  const sectionId = field.section_id ? Number(field.section_id) : null;

  // Get the highest order position
  const { data: fields } = await supabase
    .from('template_fields')
    .select('order_position')
    .eq('template_id', templateId)
    .eq('section_id', sectionId)
    .order('order_position', { ascending: false })
    .limit(1);

  const orderPosition = fields && fields.length > 0
    ? fields[0].order_position + 1
    : 0;

  const { data, error } = await supabase
    .from('template_fields')
    .insert([
      {
        template_id: templateId,
        section_id: sectionId,
        field_name: field.field_name,
        field_type: field.field_type,
        is_required: field.is_required !== undefined ? field.is_required : false,
        default_value: field.default_value || null,
        options: field.options || null,
        validation_rules: field.validation_rules || null,
        order_position: field.order_position !== undefined ? field.order_position : orderPosition,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating template field:', error);
    throw error;
  }

  return data;
};

/**
 * Update template field
 * @param fieldId - Field ID
 * @param field - Updated field data
 * @param templateId - Template ID (for cache clearing)
 * @returns Promise with updated field
 */
export const updateTemplateField = async (
  fieldId: number | string,
  field: Partial<TemplateField>,
  templateId: number | string
): Promise<TemplateField> => {
  try {
    // Log the request for debugging
    console.log('Updating template field:', fieldId, field);

    const { data, error, status, statusText } = await supabase
      .from('template_fields')
      .update(field)
      .eq('id', fieldId)
      .select()
      .single();

    if (error) {
      console.error('Error updating template field:', error);
      console.error('Status:', status, statusText);
      throw new Error(`Failed to update template field: ${error.message} (${status} ${statusText})`);
    }

    if (!data) {
      throw new Error('No data returned from update operation');
    }

    return data;
  } catch (error) {
    console.error('Exception in updateTemplateField:', error);
    throw error;
  }
};

/**
 * Delete template field
 * @param fieldId - Field ID
 * @param templateId - Template ID (for cache clearing)
 */
export const deleteTemplateField = async (
  fieldId: number | string,
  templateId: number | string
): Promise<void> => {
  const { error } = await supabase
    .from('template_fields')
    .delete()
    .eq('id', fieldId);

  if (error) {
    console.error('Error deleting template field:', error);
    throw error;
  }

  // Cache clearing handled by TanStack Query
};

/**
 * Create complete template with sections and fields in a single transaction
 * @param payload - The full template structure including sections and fields
 * @returns Promise with the created template
 */
export const createCompleteTemplate = async (payload: any): Promise<Template> => {
  const { data, error } = await supabase.rpc('create_complete_template', { payload });

  if (error) {
    console.error('Error creating complete template:', error);
    throw error;
  }

  return data;
};